<template>
  <div class="lab">
    <header class="lab-bar">
      <router-link to="/" class="brand">
        <img src="@/assets/brand/logo-hexapla-light.svg" alt="The Hexapla Institute" />
        <span class="lab-name">Labs</span>
      </router-link>
      <nav v-if="signedIn" class="sections">
        <router-link to="/" exact-active-class="is-active">Browse</router-link>
        <router-link to="/search" active-class="is-active">Search</router-link>
        <router-link to="/relations" active-class="is-active">Relations</router-link>
      </nav>
      <div v-if="signedIn" class="who">
        <span>{{ name }}</span>
        <button class="sign-out" @click="leave">Sign out</button>
      </div>
    </header>
    <div class="lab-body">
      <RelationVocabularyGate v-if="signedIn">
        <router-view />
      </RelationVocabularyGate>
      <router-view v-else />
    </div>
    <footer v-if="signedIn" class="lab-foot">
      <router-link to="/credits">Sources and credits</router-link>
    </footer>
    <HoverCard />
    <IndexPanel />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue'
import { useRouter } from 'vue-router'
import HoverCard from './components/HoverCard.vue'
import IndexPanel from './components/IndexPanel.vue'
import RelationVocabularyGate from '@/components/global/RelationVocabularyGate.vue'
import { logout } from '@/hexapla-core/domains/accounts/auth'
import { useApiUrl } from '@/compositions/servicebuilder/useApiUrl'
import { closeIndex } from './indexState'
import { session, signOut } from './session'

export default defineComponent({
  name: 'LabApp',
  components: { HoverCard, IndexPanel, RelationVocabularyGate },
  setup() {
    const router = useRouter()
    const signedIn = computed(() => Boolean(session.token))
    const name = computed(() => session.contributor?.name ?? '')
    // Ends Labs' session on the server too; the portal's is left alone.
    const leave = async () => {
      closeIndex()
      await logout(useApiUrl()).catch(() => undefined)
      signOut()
      router.push('/login')
    }
    return { signedIn, name, leave }
  },
})
</script>

<style lang="scss">
@import '@/scss/app';
</style>

<style lang="scss" scoped>
@import '@/_colors';

.lab {
  min-height: 100vh;
  background: $surface-page;
}

.lab-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 30;
  height: 4em;
  padding: 0 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: $chrome;
  color: $chrome-text;

  .brand {
    display: flex;
    align-items: center;
    gap: 0.75rem;

    img {
      max-height: 2.5em;
    }
  }

  .lab-name {
    color: $chrome-text-hover;
    font-size: 1.1rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .sections {
    display: flex;
    gap: 1.25rem;
    margin-left: 2rem;
    margin-right: auto;

    a {
      color: $chrome-text;
      font-size: 0.95rem;
      padding: 0.2rem 0;
      border-bottom: 2px solid transparent;

      &:hover {
        color: $chrome-text-hover;
      }

      &.is-active {
        color: $chrome-text-hover;
        border-bottom-color: $chrome-text-hover;
      }
    }
  }

  .who {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 0.85rem;
  }

  .sign-out {
    border: 1px solid $chrome-text-hover;
    background: none;
    color: $chrome-text;
    border-radius: 3px;
    padding: 0.15rem 0.6rem;
    cursor: pointer;

    &:hover {
      color: $chrome-text-hover;
    }
  }
}

.lab-body {
  padding-top: 4em;
}

.lab-foot {
  padding: 1.5rem 1rem 2rem;
  text-align: center;
  font-size: 0.8rem;

  a {
    color: $text-light;
  }
}
</style>
